/* global React, Appear, CasePlaceholder, CaseBreadcrumb, FooterCaseCondensed, LangToggle */
const { useEffect } = React;

// ─── Más trabajo con Dow Jones (3er nivel, desde el caso DJY Pro) ────────
function CaseDJYMore({ onBack, onBackToCase, lang = "es", setLang }) {
  const t = (es, en) => (lang === "en" ? en : es);
  return (
    <div className="case-page case-more-page">
      <header className="process-nav">
        <div className="process-nav-inner shell">
          <CaseBreadcrumb
            onBackToProjects={onBack}
            parent="DJY Pro"
            onBackToParent={onBackToCase}
            lang={lang}
          />
          <span className="nav-brand">
            <span className="mark mono">anita<em>.</em>sevilla<em>.</em>ull</span>
          </span>
          <div className="process-nav-end">
            <span className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>
              {t("caso · djy pro · más trabajo", "case · djy pro · more work")}
            </span>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="case-hero shell">
        <div className="tag tag-line">{t("caso 01 djy pro · más trabajo DJ", "case 01 djy pro · more DJ work")}</div>
        <h1 className="case-title mono">
          {t("Más trabajo con Dow Jones", "More work with Dow Jones")}
        </h1>
        <p className="case-sub">
          {t(
            "Tres proyectos más de mi tiempo en Dow Jones. Problemas distintos, misma forma de trabajar: entender qué está pasando antes de decidir qué cambiar.",
            "Three more projects from my time at Dow Jones. Different problems, same way of working: understand what's going on before deciding what to change."
          )}
        </p>
      </section>

      {/* 1 — MARKETWATCH */}
      <Appear className="case-section shell" id="marketwatch">
        <div className="section-head">
          <span className="tag">{t("// 01 · MARKETWATCH — REDISEÑO DE MARKET DATA STRIP", "// 01 · MARKETWATCH — MARKET DATA STRIP REDESIGN")}</span>
        </div>
        {lang === "en" ? (
          <h2 className="case-h3 mono">
            Reducing <span className="case-hand">cognitive load</span> for 75
            million users
          </h2>
        ) : (
          <h2 className="case-h3 mono">
            Reducir la <span className="case-hand">carga cognitiva</span> para 75
            millones de usuarios
          </h2>
        )}

        <div className="case-mw-ba">
          <CasePlaceholder
            className="case-fig--bare case-mw-ba-fig"
            src="assets/cases/mas-trabajos-dj/antes MW"
            alt={t(
              "Captura del Market Data Strip original de MarketWatch: índices, precios y tickers acumulados con once variables tipográficas distintas, sin jerarquía visual clara.",
              "Original MarketWatch Market Data Strip: indexes, prices and tickers stacked with eleven different type variables, no clear visual hierarchy."
            )}
            caption={t("Antes — Market Data Strip original", "Before — original Market Data Strip")}
            ratio="auto"
          />
          <CasePlaceholder
            className="case-fig--bare case-mw-ba-fig"
            src="assets/cases/mas-trabajos-dj/despues MW"
            alt={t(
              "Captura del Market Data Strip rediseñado de MarketWatch: tres secciones separadas por chunking visual, tipografía estandarizada y jerarquía explícita dentro de cada bloque.",
              "Redesigned MarketWatch Market Data Strip: three sections separated by visual chunking, standardized typography and explicit hierarchy within each block."
            )}
            caption={t("Después — Market Data Strip rediseñado", "After — redesigned Market Data Strip")}
            ratio="auto"
          />
        </div>

        <div className="case-mw-text-result">
          <div className="case-prose">
            {lang === "en" ? (
              <React.Fragment>
                <p>
                  MarketWatch gets over 75 million visits a month. Its data strip
                  — the financial info bar at the top of the page —{" "}
                  <strong>
                    was the first contact with the market for millions of users.
                    And it was losing them.
                  </strong>
                </p>
                <p>
                  The strip packed indexes, prices, percentages and tickers into
                  a small space using 11 different type variables: no hierarchy,
                  no separation, no context.{" "}
                  <strong>
                    For someone without financial experience, that density
                    doesn't inform — it overwhelms.
                  </strong>{" "}
                  Two interaction problems added friction: the central chart
                  changed on hover over indexes without that relationship being
                  obvious, and the scroll controls used the same icon as the
                  market up/down indicators.
                </p>
                <p>
                  The constraint was clear: live product, 75M users with mental
                  models already formed.{" "}
                  <strong>You couldn't redesign — you could optimize.</strong>{" "}
                  The strategy was minimal change, maximum impact: visual
                  chunking to separate the strip's three sections, type
                  standardization to cut noise, explicit hierarchy inside each
                  section, and fixing the ambiguous icons. All the original
                  information stayed.
                </p>
                <p>
                  Recognition time dropped from 37.8 to 12.5 seconds.{" "}
                  <strong>
                    Cognitive load didn't come down by removing data — it came
                    down by giving the eye a clear path through it.
                  </strong>
                </p>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <p>
                  MarketWatch tiene más de 75 millones de visitas mensuales. Su data
                  strip — la barra de información financiera en el top de la página —{" "}
                  <strong>
                    era el primer contacto con el mercado para millones de
                    usuarios. Y los estaba perdiendo.
                  </strong>
                </p>
                <p>
                  El strip acumulaba índices, precios, porcentajes y tickers en poco
                  espacio usando 11 variables tipográficas distintas: sin jerarquía,
                  sin separación, sin contexto.{" "}
                  <strong>
                    Para alguien sin experiencia financiera, la densidad no informa:
                    abruma.
                  </strong>{" "}
                  Dos problemas de interacción sumaban fricción: el gráfico central
                  cambiaba al hacer hover sobre los índices sin que esa relación
                  fuera obvia, y los controles de scroll usaban el mismo ícono que
                  los indicadores de suba/baja del mercado.
                </p>
                <p>
                  El constraint era claro: producto vivo, 75M de usuarios con mapas
                  mentales formados.{" "}
                  <strong>No se podía rediseñar — se podía optimizar.</strong> La
                  estrategia fue minimal change, máximo impacto: chunking visual
                  para separar las tres secciones del strip, estandarización
                  tipográfica para eliminar ruido, jerarquía explícita dentro de
                  cada sección, y corrección de los íconos ambiguos. Toda la
                  información original se mantuvo.
                </p>
                <p>
                  El tiempo de reconocimiento bajó de 37.8 a 12.5 segundos.{" "}
                  <strong>
                    La carga cognitiva no se redujo quitando datos — se redujo
                    dándole al ojo una ruta clara para recorrerlos.
                  </strong>
                </p>
              </React.Fragment>
            )}
            <p className="case-mw-cta">
              <a
                className="link-tertiary mono"
                href="https://www.figma.com/design/9QmhWdzNyO6vZdbZ3VDsGR/DJones--demo-?node-id=29-2835&t=bGxXYfFFH8mVP0uE-1"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("Ver proyecto completo en Figma", "See the full project in Figma")} <span aria-hidden="true">→</span>
              </a>
            </p>
          </div>

          {/* Sticky result strip — accompanies the prose as it scrolls.
              The only place teal appears on this page. */}
          <aside className="case-mw-result-wrap">
            <div className="case-result-strip case-result-strip--centered">
              <div className="case-result-delta-big mono">−67%</div>
              <div className="case-result-pair">
                <span className="case-result-from mono">37.8s</span>
                <span className="case-result-arrow mono" aria-hidden="true">→</span>
                <span className="case-result-to mono">12.5s</span>
              </div>
              <div className="case-result-label mono">
                {t("tiempo de reconocimiento — los mismos datos, otra estructura", "recognition time — same data, different structure")}
              </div>
            </div>
          </aside>
        </div>
      </Appear>

      {/* 2 — AUDITORÍA DE ARTÍCULOS */}
      <Appear className="case-section shell" id="articles-audit">
        <div className="section-head">
          <span className="tag">{t("// 02 · AUDITORÍA DE ARTÍCULOS MULTIPLATAFORMA", "// 02 · CROSS-PLATFORM ARTICLE AUDIT")}</span>
        </div>
        {lang === "en" ? (
          <h2 className="case-h3 mono">
            One article. <span className="case-hand">Eleven products.</span> Zero
            consistency.
          </h2>
        ) : (
          <h2 className="case-h3 mono">
            Un artículo. <span className="case-hand">Once productos.</span> Cero
            consistencia.
          </h2>
        )}

        <div className="case-articles-grid">
          {/* Sticky prose — follows scroll along the tall infographic. */}
          <aside className="case-articles-prose-wrap">
            <div className="case-prose">
            {lang === "en" ? (
              <React.Fragment>
                <p>
                  Dow Jones publishes content across eleven products:{" "}
                  <strong>WSJ, Barron's, MarketWatch, FN London, WSJ+, Factiva,
                  Giants, Risk &amp; Compliance Journal, NewsPlus, MarketSurge and
                  OpenFin.</strong> Each one displayed the same article components
                  — author, date, links, tags — in its own way. There was no
                  standard.
                </p>
                <p>
                  It wasn't a visual-inconsistency problem. It was systemic. The
                  same component meant different things in different products. An
                  author byline on WSJ behaved differently than on Factiva —
                  different hierarchy, different interactions, different visible
                  metadata. Multiplied across eleven products, the design system
                  was more a promise than a reality.
                </p>
                <p>
                  With the UXA team, we mapped every article component across the
                  eleven products. For each component: how it was displayed, what
                  information it held, how it behaved, and where it diverged from
                  the rest.{" "}
                  <strong>The deliverable wasn't a redesign — it was structured
                  documentation</strong> so the design-system teams could make
                  decisions.
                </p>
                <p>
                  The problem with a fragmented design system is that nobody sees
                  it until somebody maps it. Documenting how eleven products
                  treat the same component is the base that lets you unify
                  criteria without starting from zero every time.{" "}
                  <em>It's invisible work until it stops being invisible.</em>
                </p>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <p>
                  Dow Jones publica contenido en once productos:{" "}
                  <strong>WSJ, Barron's, MarketWatch, FN London, WSJ+, Factiva,
                  Giants, Risk &amp; Compliance Journal, NewsPlus, MarketSurge y
                  OpenFin.</strong> Cada uno mostraba los mismos componentes de
                  artículo — autor, fecha, links, tags — a su manera. No había
                  estándar.
                </p>
                <p>
                  No era un problema de inconsistencia visual. Era sistémico. El
                  mismo componente significaba cosas distintas en productos
                  distintos. Una firma de autor en WSJ se comportaba diferente
                  que en Factiva — otra jerarquía, otras interacciones, otro
                  metadata visible. Multiplicado por once productos, el design
                  system era más una promesa que una realidad.
                </p>
                <p>
                  Con el equipo UXA, mapeamos cada componente de artículo en los
                  once productos. Para cada componente: cómo se mostraba, qué
                  información contenía, cómo se comportaba, y dónde divergía del
                  resto.{" "}
                  <strong>El entregable no era un rediseño — era documentación
                  estructurada</strong> para que los equipos de design system
                  pudieran tomar decisiones.
                </p>
                <p>
                  El problema con un design system fragmentado es que nadie lo
                  ve hasta que alguien lo mapea. Documentar cómo once productos
                  distintos tratan el mismo componente es la base que permite
                  unificar criterios sin empezar desde cero cada vez.{" "}
                  <em>Es trabajo invisible hasta que deja de serlo.</em>
                </p>
              </React.Fragment>
            )}
            </div>
          </aside>

          <CasePlaceholder
            className="case-fig--bare case-articles-fig"
            src="assets/cases/mas-trabajos-dj/auditoria articulos"
            alt={t(
              "Auditoría de artículos multiplataforma: anatomía comparativa del mismo componente de artículo (autor, fecha, links, tags) en los once productos de Dow Jones, con anotaciones de jerarquía, comportamiento y metadata visible en cada caso.",
              "Cross-platform article audit: comparative anatomy of the same article component (author, date, links, tags) across the eleven Dow Jones products, with annotations on hierarchy, behavior and visible metadata in each case."
            )}
            caption={t("Auditoría de componentes de artículo en los 11 productos de Dow Jones", "Article-component audit across Dow Jones' 11 products")}
            ratio="auto"
          />
        </div>
      </Appear>

      {/* 3 — BARRON'S IA AUDIT */}
      <Appear className="case-section shell" id="barrons-ia">
        <div className="section-head">
          <span className="tag">{t("// 03 · AUDITORÍA DE ARQUITECTURA DE INFORMACIÓN — BARRON'S", "// 03 · INFORMATION ARCHITECTURE AUDIT — BARRON'S")}</span>
        </div>
        {lang === "en" ? (
          <h2 className="case-h3 mono">
            When <span className="case-hand">navigation</span> doesn't work, the{" "}
            <span className="case-hand">content</span> doesn't either
          </h2>
        ) : (
          <h2 className="case-h3 mono">
            Cuando la <span className="case-hand">navegación</span> no funciona,
            el <span className="case-hand">contenido</span> tampoco
          </h2>
        )}

        <div className="case-prose">
          {lang === "en" ? (
            <React.Fragment>
              <p>
                As part of the UXA team, I joined a full
                information-architecture audit of <strong>Barron's</strong>. My
                focus was the navigation systems: global, local, contextual and
                utility — mapping inconsistencies, broken patterns and
                improvement opportunities across the different user types
                (subscribers, financial advisors, students).
              </p>
              <p>
                <strong>
                  The work covered a current-state assessment, structured
                  findings, and actionable recommendations for each layer of the
                  navigation system.
                </strong>
              </p>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <p>
                Como parte del equipo UXA, participé en una auditoría completa de
                arquitectura de información de <strong>Barron's</strong>. Mi foco
                fue el análisis de los sistemas de navegación: navegación global,
                local, contextual y de utilidad — mapeando inconsistencias,
                patrones rotos y oportunidades de mejora a través de los distintos
                tipos de usuario (suscriptores, asesores financieros, estudiantes).
              </p>
              <p>
                <strong>
                  El trabajo incluyó relevamiento de estado actual, hallazgos
                  estructurados y recomendaciones accionables para cada capa del
                  sistema de navegación.
                </strong>
              </p>
            </React.Fragment>
          )}
        </div>

        <figure className="case-fig case-fig--bare case-barrons-fig">
          <div className="case-img-wrap">
            <video
              src={encodeURI("assets/cases/mas-trabajos-dj/audit barrons.webm")}
              aria-label={t(
                "Auditoría de arquitectura de información de Barron's: recorrido animado por los sistemas de navegación global, local, contextual y de utilidad, mostrando inconsistencias y patrones rotos a través de los distintos tipos de usuario.",
                "Barron's information-architecture audit: animated walkthrough of the global, local, contextual and utility navigation systems, surfacing inconsistencies and broken patterns across user types."
              )}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
            />
          </div>
          <figcaption className="mono">
            {t("Auditoría de IA en Barron's — recorrido por los sistemas de navegación", "Barron's IA audit — walkthrough of the navigation systems")}
          </figcaption>
        </figure>
      </Appear>

      {/* 4 — WSJ PLUS (acotado) */}
      <Appear className="case-section case-section--brief shell" id="wsj-plus">
        <div className="section-head">
          <span className="tag">{t("// 04 · WSJ PLUS", "// 04 · WSJ PLUS")}</span>
        </div>
        {lang === "en" ? (
          <h2 className="case-h3 mono">
            Work nobody sees, but that{" "}
            <span className="case-hand">holds up</span> what everyone uses.
          </h2>
        ) : (
          <h2 className="case-h3 mono">
            Trabajo que nadie ve, pero{" "}
            <span className="case-hand">sostiene</span> lo que todos usan.
          </h2>
        )}
        <div className="case-prose">
          {lang === "en" ? (
            <React.Fragment>
              <p>
                WSJ Plus is The Wall Street Journal's premium benefits program —
                a value layer communicated exclusively through a subscription
                landing page.{" "}
                <strong>
                  My contribution: copy review, feedback on information
                  hierarchy, and documentation of the design decisions the team
                  made.
                </strong>
              </p>
              <p>
                It's not the kind of work that defines a role, but it is the
                kind that holds it up and validates it. At this scale, the
                consistency between what the product promises and what the user
                actually experiences also depends on someone reviewing with
                judgment before things ship. That's what I did here.
              </p>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <p>
                WSJ Plus es el programa de beneficios premium de The Wall Street
                Journal — una capa de valor que se comunica exclusivamente a
                través de una landing page de suscripción.{" "}
                <strong>
                  Mi participación fue: revisión de copy, feedback sobre jerarquía
                  de información y documentación de decisiones de diseño tomadas
                  por el equipo.
                </strong>
              </p>
              <p>
                No es el tipo de trabajo que define un rol, pero sí el que lo
                sostiene y valida. En productos de esta escala, la consistencia
                entre lo que el producto promete y lo que el usuario experimenta
                depende también de quien revisa con criterio antes de que algo
                salga. Eso fue lo que hice acá.
              </p>
            </React.Fragment>
          )}
        </div>
      </Appear>

      {/* FOOT */}
      <FooterCaseCondensed
        otherCaseLabel={t("Ver caso agente IA", "See AI agent case")}
        otherCaseHref="#caso-agente-ia"
        lang={lang}
      />
    </div>
  );
}

window.CaseDJYMore = CaseDJYMore;
